import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CONSENT_KEY, OPEN_EVENT, readConsent, saveConsent, startAnalytics, stopAnalytics, clearAnalyticsCookies } from '../services/privacy';
import '../assets/css/Privacy.css';

// Tempo que a confirmação fica na tela antes do aviso se fechar sozinho. Curto o bastante
// para não travar a navegação, longo o bastante para a pessoa ler o que foi salvo.
const FECHAR_APOS = 1600;

function formatarData(at) {
  try {
    return new Date(at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return ''; }
}

export default function PrivacyPreferences() {
  const [choice, setChoice] = useState(readConsent);
  const [open, setOpen] = useState(() => !readConsent());
  const [salvo, setSalvo] = useState(null);
  const [error, setError] = useState('');
  const location = useLocation();
  useEffect(() => {
    function synchronize(event) {
      if (event && event.key !== null && event.key !== CONSENT_KEY) return;
      const current = readConsent();
      if (!current?.analytics) {
        stopAnalytics();
        clearAnalyticsCookies();
        if (document.getElementById('privacy-analytics')) window.location.reload();
      }
      setChoice(previous => previous?.at === current?.at ? previous : current);
      if (!current) setOpen(true);
    }
    synchronize();
    window.addEventListener('storage', synchronize);
    const timer = setInterval(synchronize, 60000);
    return () => { window.removeEventListener('storage', synchronize); clearInterval(timer); };
  }, []);
  useEffect(() => {
    if (location.pathname === '/' && choice?.analytics) startAnalytics();
    else stopAnalytics();
    return stopAnalytics;
  }, [location.pathname, choice]);
  // Once the library has loaded, a full navigation unloads its automatic listeners
  // before any private screen is rendered (including browser back/forward).
  useEffect(() => {
    if (location.pathname !== '/' && document.getElementById('privacy-analytics')) {
      window.location.reload();
    }
  }, [location.pathname]);

  const abrir = useCallback(() => { setSalvo(null); setError(''); setOpen(true); }, []);
  const fechar = useCallback(() => { setSalvo(null); setOpen(false); }, []);

  // A página de privacidade pede a abertura por evento: a escolha vive aqui, não lá.
  useEffect(() => {
    window.addEventListener(OPEN_EVENT, abrir);
    return () => window.removeEventListener(OPEN_EVENT, abrir);
  }, [abrir]);

  // Só dá para sair pelo Esc depois de escolher: sem escolha, fechar não significa nada.
  useEffect(() => {
    if (!open || !choice) return undefined;
    function aoTeclar(event) { if (event.key === 'Escape') fechar(); }
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [open, choice, fechar]);

  // Fecha sozinho depois de salvar, para a escolha não virar um aviso preso na tela.
  useEffect(() => {
    if (salvo === null) return undefined;
    const timer = setTimeout(() => { setSalvo(null); setOpen(false); }, FECHAR_APOS);
    return () => clearTimeout(timer);
  }, [salvo]);

  function choose(analytics) {
    try {
      const hadAnalytics = !!document.getElementById('privacy-analytics');
      setChoice(saveConsent(analytics));
      setSalvo(analytics);
      setError('');
      if (!analytics && hadAnalytics) window.location.reload();
    } catch {
      stopAnalytics();
      setSalvo(null);
      setError('Não foi possível salvar sua preferência. As estatísticas permanecem desativadas.');
    }
  }

  const ativo = choice?.analytics === true;
  const decidiu = !!choice;
  const estado = !decidiu ? 'Ainda não escolhido' : ativo ? 'Estatísticas ativadas' : 'Estatísticas desativadas';
  return <>
    {/* Depois da escolha o botão fixo sai da tela — ficar para sempre num canto incomoda mais
        do que ajuda. Quem quiser mudar vai por "Privacidade e seus direitos" (rodapé), e a
        própria página reabre este painel pelo evento. */}
    {!decidiu && !open && <button
      className="privacy-settings privacy-settings--pendente"
      onClick={abrir}
      aria-expanded={open}
      title={estado}>
      <span className="privacy-dot privacy-dot--pendente" aria-hidden="true" />
      Preferências de privacidade
    </button>}
    {open && <section className="privacy-banner" aria-label="Preferências de privacidade">
      <header className="privacy-banner__topo">
        <span className={`privacy-badge privacy-badge--${!decidiu ? 'pendente' : ativo ? 'on' : 'off'}`}>
          <span className={`privacy-dot privacy-dot--${!decidiu ? 'pendente' : ativo ? 'on' : 'off'}`} aria-hidden="true" />
          {estado}
        </span>
        {decidiu && <button className="privacy-fechar" onClick={fechar} aria-label="Fechar preferências">×</button>}
      </header>

      <h2>Estatísticas do site</h2>
      {/* O detalhe (o que é necessário, prazos, direitos) vive em /privacidade: o painel só
          precisa da escolha, senão vira uma parede de texto em cima da página. */}
      <p>Com sua autorização, usamos o Google Analytics na página inicial para medir visitas.
        Recusar não impede o uso do site.</p>

      {decidiu && <p className="privacy-desde">Registrada em {formatarData(choice.at)} · vale 180 dias.</p>}

      <Link className="privacy-saiba" to="/privacidade">Saiba mais sobre seus dados</Link>
      {error && <p className="privacy-erro" role="alert">{error}</p>}

      {salvo === null
        ? <div className="privacy-actions">
            <button
              className="privacy-btn privacy-btn--recusar"
              aria-pressed={decidiu && !ativo}
              onClick={() => choose(false)}>Recusar estatísticas</button>
            <button
              className="privacy-btn privacy-btn--aceitar"
              aria-pressed={ativo}
              onClick={() => choose(true)}>Aceitar estatísticas</button>
          </div>
        : <p className="privacy-salvo" role="status">
            Preferência salva: estatísticas {salvo ? 'ativadas' : 'desativadas'}. Para mudar depois,
            use “Privacidade e seus direitos” no rodapé.
          </p>}
    </section>}
  </>;
}
